import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractSocialMediaChannel } from "@similarweb/pro-api-client";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge, Carousel } from "@similarweb/ui-components";

import { AlertsGroupCarousel } from "../../../common/AlertsGroupCarousel";
import { CardLayout } from "../../../common/card-layout/CardLayout";
import { ChannelIcon } from "../../../common/ChannelIcon";
import { useSelectedGroupItemStore } from "../../../common/modal/useSelectedGroupItemStore";
import { SeeAllButton } from "../../../common/SeeAllButton";
import type { SocialPostData } from "../types";

import { LazySocialPost } from "./LazySocialPost";
import { SeeMoreCard } from "./SeeMoreCard";

interface ISocialPostsAlertProps {
    groupIndex: number;
    channel: ActivationAlertsContractSocialMediaChannel;
    domain: string;
    posts: SocialPostData[];
    totalPostsCount: number;
    favicon: string;
}

export const SocialPostsAlert = ({
    groupIndex,
    channel,
    domain,
    posts,
    totalPostsCount,
    favicon,
}: ISocialPostsAlertProps) => {
    const { translate } = useTranslate();
    const { track } = useTracking();
    const setSelectedGroupItem = useSelectedGroupItemStore((store) => store.setSelectedGroupItem);

    const hasMorePosts = totalPostsCount > posts.length;

    const handlePostClick = (index: number) => {
        track("Alert Card", "click", "open alert/alerts/social-posts");
        setSelectedGroupItem({ itemIndex: index, groupIndex });
    };
    const handleSeeAllClick = () => {
        track("Alert Card", "click", "open alert/alerts/social-posts");
        setSelectedGroupItem({ itemIndex: 0, groupIndex });
    };
    const handleSeeMoreClick = () => {
        track("Alert Card", "click", "open alert/alerts/social-posts");
        setSelectedGroupItem({ itemIndex: posts.length, groupIndex });
    };

    return (
        <CardLayout
            iconUrl={favicon}
            onClick={() => handlePostClick(0)}
            data-automation="social-posts-alert"
            data-domain={domain}
        >
            <CardLayout.Header>
                <Badge data-automation="alert-badge">{channel}</Badge>
                <CardLayout.Title>
                    {translate(`activation.alerts.cards.social-posts.titles.${channel.toLowerCase()}`, {
                        count: totalPostsCount,
                        domain,
                    })}
                </CardLayout.Title>
                <ChannelIcon channel={channel} />
            </CardLayout.Header>
            <CardLayout.Content>
                <AlertsGroupCarousel>
                    {posts.map((post, index) => (
                        <Carousel.Item key={post.id}>
                            <LazySocialPost
                                post={post}
                                index={index}
                                onClick={() => handlePostClick(index)}
                            />
                        </Carousel.Item>
                    ))}
                    {hasMorePosts ? (
                        <Carousel.Item key="see-more-card">
                            <SeeMoreCard
                                domain={domain}
                                socialMediaChannel={channel}
                                onClick={handleSeeMoreClick}
                            />
                        </Carousel.Item>
                    ) : null}
                </AlertsGroupCarousel>
                <SeeAllButton
                    onClick={handleSeeAllClick}
                    isDisabled={!posts.length}
                    dataAutomation="see-all-posts-button"
                >
                    {translate("activation.alerts.cards.social-posts.see-all-posts")}
                </SeeAllButton>
            </CardLayout.Content>
        </CardLayout>
    );
};
